"use client"

import { useEffect, useState } from "react"

export interface SlowReplyState {
  /** A handoff has fired in this conversation. */
  handedOff: boolean
  /** A human has replied since the handoff. */
  humanReplied: boolean
  /** We already have the visitor's email. */
  emailProvided: boolean
  /** The visitor dismissed the ask. */
  dismissed: boolean
  /** Threshold from glue's `session` event. 0 disables the prompt entirely. */
  thresholdMs: number
  /** Milliseconds since the handoff fired. */
  elapsedMs: number
}

/**
 * Whether to offer the visitor an optional email address. Pure (no timers, no
 * DOM) so every branch is unit-testable, matching the `nextSeenMark` pattern.
 *
 * `thresholdMs === 0` is glue's kill switch and must be checked before the
 * elapsed comparison — otherwise "disabled" would evaluate as "prompt
 * immediately", which is the opposite of the intent.
 */
export function shouldPromptForEmail(s: SlowReplyState): boolean {
  if (s.thresholdMs <= 0) return false
  if (!s.handedOff) return false
  if (s.humanReplied) return false
  if (s.emailProvided) return false
  if (s.dismissed) return false
  return s.elapsedMs >= s.thresholdMs
}

interface UseSlowReplyPromptArgs {
  handedOff: boolean
  humanReplied: boolean
  emailProvided: boolean
  dismissed: boolean
  thresholdMs: number
  /** Epoch ms when the handoff fired, or null if none has. */
  handoffAt: number | null
}

/**
 * Shows an optional email ask when a handoff has happened and no human has
 * replied within the threshold.
 *
 * A browser-side timer is the right home for this: if the tab is gone there is
 * nobody to ask, so surviving the tab buys nothing. The threshold itself comes
 * from glue so it stays tunable without a deploy here.
 */
export function useSlowReplyPrompt({
  handedOff,
  humanReplied,
  emailProvided,
  dismissed,
  thresholdMs,
  handoffAt,
}: UseSlowReplyPromptArgs): boolean {
  const [due, setDue] = useState(false)

  useEffect(() => {
    if (thresholdMs <= 0 || !handedOff || handoffAt === null) {
      setDue(false)
      return
    }
    if (humanReplied || emailProvided || dismissed) {
      setDue(false)
      return
    }

    const check = (): void => {
      setDue(
        shouldPromptForEmail({
          handedOff,
          humanReplied,
          emailProvided,
          dismissed,
          thresholdMs,
          elapsedMs: Date.now() - handoffAt,
        })
      )
    }

    check()
    const remaining = Math.max(0, thresholdMs - (Date.now() - handoffAt))
    const id = window.setTimeout(check, remaining)
    return () => window.clearTimeout(id)
  }, [
    handedOff,
    humanReplied,
    emailProvided,
    dismissed,
    thresholdMs,
    handoffAt,
  ])

  return due
}
